import { useState, useEffect } from 'react';
import { Send, Mail, User, MessageSquare } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import api from '../utils/api';
import { validateFormField, isHoneypotFilled, HONEYPOT_FIELD, checkRateLimit, markFormLoaded, isSubmitTooFast, sanitizeText } from '../utils/formValidation';

export default function GhostwriterContact() {
  const [form, setForm] = useState({ name: '', email: '', message: '', [HONEYPOT_FIELD]: '' });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { markFormLoaded('contact'); }, []);

  const validate = () => {
    const e = {};
    const nameCheck = validateFormField(form.name, 'name');
    if (!nameCheck.valid) e.name = nameCheck.error;
    const emailCheck = validateFormField(form.email, 'email');
    if (!emailCheck.valid) e.email = emailCheck.error;
    const msgCheck = validateFormField(form.message, 'message');
    if (!msgCheck.valid) e.message = msgCheck.error;
    if (!form.message.trim() || form.message.trim().length < 10) e.message = 'Message must be at least 10 characters.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isHoneypotFilled(form[HONEYPOT_FIELD])) return;
    if (isSubmitTooFast('contact')) return;
    if (!checkRateLimit('contact', 5000)) { setErrors({ form: 'Please wait a moment before submitting again.' }); return; }
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post('/ghostwriter/contact', {
        name: sanitizeText(form.name),
        email: sanitizeText(form.email),
        message: sanitizeText(form.message)
      });
      setSent(true);
    } catch { setErrors({ form: 'Failed to send. Please try again.' }); }
    finally { setLoading(false); }
  };

  return (
    <>
      <SeoHead
        title="Contact AI Ghostwriter Pro — Get a Free Ghostwriting Consultation"
        description="Contact AI Ghostwriter Pro to discuss your book project. Get a free consultation about our ghostwriting services. Tell us about your novel, memoir, non-fiction, or business book idea."
        keywords="contact ghostwriter, hire a ghostwriter contact, ghostwriting consultation, book ghostwriter inquiry, ghostwriting quote, free ghostwriting consultation"
        path="/contact"
      />

      <section className="pt-28 pb-20 md:pt-40 md:pb-32">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12 md:mb-16">
            <p className="font-sans uppercase tracking-[0.2em] text-xs font-bold text-primary mb-4">Contact</p>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight mb-4 md:mb-6">Let's Talk About<br /><span className="text-primary">Your Book</span></h1>
            <p className="text-muted-foreground leading-relaxed text-sm md:text-base">Have questions? Want to discuss your project? We'd love to hear from you.</p>
          </div>

          {sent ? (
            <div className="text-center py-12 md:py-16 bg-white/5 border border-white/10 rounded-sm" data-testid="contact-success">
              <Send className="w-10 h-10 md:w-12 md:h-12 text-primary mx-auto mb-4" />
              <h3 className="font-serif text-xl md:text-2xl mb-2">Message Sent!</h3>
              <p className="text-muted-foreground text-sm">We'll get back to you within 24 hours.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6" data-testid="contact-form" noValidate>
              {errors.form && <p className="text-sm text-red-400 text-center">{errors.form}</p>}
              {/* Honeypot - hidden from humans */}
              <div className="absolute opacity-0 h-0 w-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
                <input type="text" name={HONEYPOT_FIELD} value={form[HONEYPOT_FIELD]} onChange={(e) => setForm({...form, [HONEYPOT_FIELD]: e.target.value})} autoComplete="off" tabIndex={-1} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input type="text" required value={form.name}
                    onChange={(e) => { setForm({...form, name: e.target.value}); if (errors.name) setErrors({...errors, name: null}); }}
                    data-testid="contact-name"
                    className={`w-full pl-10 pr-4 h-12 bg-white/5 border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:ring-1 focus:ring-primary/50 outline-none transition-colors ${errors.name ? 'border-red-500/50 focus:border-red-500' : 'border-white/10 focus:border-primary'}`}
                    placeholder="Your name" maxLength={100}
                  />
                </div>
                {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input type="email" required value={form.email}
                    onChange={(e) => { setForm({...form, email: e.target.value}); if (errors.email) setErrors({...errors, email: null}); }}
                    data-testid="contact-email"
                    className={`w-full pl-10 pr-4 h-12 bg-white/5 border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:ring-1 focus:ring-primary/50 outline-none transition-colors ${errors.email ? 'border-red-500/50 focus:border-red-500' : 'border-white/10 focus:border-primary'}`}
                    placeholder="your@email.com" maxLength={254}
                  />
                </div>
                {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Message</label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-4 w-4 h-4 text-muted-foreground" />
                  <textarea required rows={5} value={form.message}
                    onChange={(e) => { setForm({...form, message: e.target.value}); if (errors.message) setErrors({...errors, message: null}); }}
                    data-testid="contact-message"
                    className={`w-full pl-10 pr-4 py-3 bg-white/5 border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:ring-1 focus:ring-primary/50 outline-none resize-none transition-colors ${errors.message ? 'border-red-500/50 focus:border-red-500' : 'border-white/10 focus:border-primary'}`}
                    placeholder="Tell us about your book project..." maxLength={5000}
                  />
                </div>
                {errors.message && <p className="text-xs text-red-400 mt-1">{errors.message}</p>}
              </div>
              <button type="submit" disabled={loading} data-testid="contact-submit"
                className="w-full rounded-sm py-3 bg-primary text-primary-foreground font-semibold tracking-wide transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.3)] hover:bg-white hover:text-black hover:shadow-[0_0_25px_rgba(255,255,255,0.5)] flex items-center justify-center gap-2">
                {loading ? 'Sending...' : <><Send className="w-4 h-4" /> Send Message</>}
              </button>
            </form>
          )}

          <div className="mt-12 md:mt-16 text-center">
            <p className="text-sm text-muted-foreground">We typically respond within 24 hours.</p>
          </div>
        </div>
      </section>
    </>
  );
}
